import type { DocumentVisual } from "../studio/visual-design.js";
import type { ClientId, EquipmentId, InspectionId, ProfessionalId } from "../../shared/kernel/identifiers.js";

/**
 * O que os relatórios de END por método (PM, US-Convencional) têm em comum
 * no desenho documental herdado do LP/ME: cada `*Id` registra QUAL cadastro
 * foi escolhido e o texto ao lado registra O QUE ele dizia naquele momento.
 * O documento imprime o texto — uma edição posterior do cadastro nunca
 * reescreve um relatório já emitido. Cada método tem o seu domínio próprio
 * (PmReport, ConventionalUsReport); este arquivo só evita duplicar o que é
 * idêntico. O LP mantém o seu arquivo — não foi tocado.
 */

/** Estados oficiais do laudo — nada é aprovado automaticamente. */
export type NdtLaudo = "APROVADO" | "REPROVADO" | "EXAME_COMPLEMENTAR" | "EM_ELABORACAO" | "";

export const NDT_LAUDO_OPTIONS: ReadonlyArray<{ value: NdtLaudo; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "APROVADO", label: "Aprovado" },
  { value: "REPROVADO", label: "Reprovado" },
  { value: "EXAME_COMPLEMENTAR", label: "Exame Complementar" },
  { value: "EM_ELABORACAO", label: "Em Elaboração" }
];

export function formatNdtLaudo(laudo: NdtLaudo | null | undefined): string {
  if (!laudo) return "";
  return NDT_LAUDO_OPTIONS.find(o => o.value === laudo)?.label ?? String(laudo);
}

/** Legenda impressa no documento (FORM-PM / FORM-USC, "LEGENDA E CRITÉRIO DE RESULTADO"). */
export const NDT_RESULT_LEGEND = "(A) APROVADO  |  (R) REPROVADO  |  (EC) EXAME COMPLEMENTAR  |  (P) EM ELABORAÇÃO";
export const NDT_RESULT_LEGEND_NOTES = [
  "O enquadramento final deverá refletir o critério técnico aplicável e os resultados efetivamente observados.",
  "Indicações relevantes deverão ser registradas individualmente no registro da inspeção."
];

export interface NdtCroqui {
  /** Data URL (image/*). */
  image: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
}

export interface NdtPhoto {
  key: string;
  image: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  /** Componente / item ("S1", "Junta 3"...). */
  item: string;
  location: string;
  comment: string;
}

export type NdtSignatoryRole = "INSPETOR" | "PLH" | "FISCAL";

export interface NdtSignatory {
  role: NdtSignatoryRole;
  professionalId: ProfessionalId | null;
  name: string;
  functionTitle: string;
  credential: string;
  signatureImageId: string | null;
  signedAt: string | null;
}

/** Rótulos do FORM-PM / FORM-USC: INSPETOR DE END · ENGENHEIRO / PLH · FISCAL DO CONTRATO. */
export const NDT_SIGNATORY_ROLE_LABELS: Record<NdtSignatoryRole, string> = {
  INSPETOR: "Inspetor de END",
  PLH: "Engenheiro / PLH",
  FISCAL: "Fiscal do Contrato"
};

export interface NdtHistoryEntry {
  at: string;
  by: string;
  action: "CREATED" | "SAVED" | "VALIDATED" | "SIGNED" | "PDF_GENERATED";
  detail: string;
}

/**
 * Situação comum aos relatórios de END. `IN_PROGRESS` (em elaboração, a
 * partir do primeiro salvamento) e `FINALIZED` (fechado pela aprovação do
 * PLH) hoje são usados pelo ME; os demais métodos seguem no fluxo antigo até
 * serem migrados, e por isso os valores continuam todos no mesmo conjunto.
 */
export type NdtReportStatus = "DRAFT" | "IN_PROGRESS" | "VALIDATED" | "SIGNED" | "FINALIZED";

/** Campos comuns aos relatórios de END por método — cada domínio os estende. */
export interface NdtReportCommon<TModel> {
  id: string;
  clientId: ClientId;
  reportNumber: string;
  clientReportNumber: string;
  revision: string;
  status: NdtReportStatus;

  projectId: string | null;
  unitAreaId: string | null;
  equipmentId: EquipmentId | null;
  inspectionId: InspectionId | null;

  clientDisplayName: string;
  projectContract: string;
  unitArea: string;
  equipmentTag: string;
  equipmentTypeLabel: string;
  component: string;
  inspectionNumber: string;
  osWorkOrderId: string | null;
  osNumber: string;
  osSubWorkOrderId: string | null;
  subOsNumber: string;
  testDate: string;
  inspectorProfessionalId: ProfessionalId | null;
  inspectorName: string;

  procedureId: string | null;
  procedureRevision: string;
  referenceNormId: string | null;
  referenceNorm: string;
  acceptanceCriteriaId: string | null;
  acceptanceCriteria: string;
  baseMaterial: string;
  testTemperature: string;

  croqui: NdtCroqui | null;
  photos: NdtPhoto[];
  observations: string;
  signatories: NdtSignatory[];

  issueDate: string;
  history: NdtHistoryEntry[];
  frozenSnapshot: TModel | null;

  signedAt: string | null;
  signedByUserId: string | null;
  signedByProfessionalId: ProfessionalId | null;

  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/** Parte comum do modelo documental — Preview e PDF consomem o mesmo objeto. */
export interface NdtDocumentCommon {
  /** Visual do STUDIO congelado com o modelo (ausente em documentos anteriores ao Studio = padrão vigente). */
  visual?: DocumentVisual | null;
  reportNumber: string;
  clientReportNumber: string;
  revision: string;
  status: NdtReportStatus;
  issueDate: string;
  osNumber: string;
  subOsNumber: string;
  qrPayload: string;
  identification: {
    client: string;
    projectContract: string;
    unitArea: string;
    equipmentTag: string;
    equipmentType: string;
    osSubOs: string;
    testDate: string;
    inspector: string;
    inspectionNumber: string;
  };
  croqui: { image: string; fileName: string } | null;
  photos: Array<{ number: string; image: string; item: string; location: string; comment: string }>;
  observations: string;
  legend: string;
  legendNotes: string[];
  responsibilityTerm: string;
  signatories: Array<{
    role: NdtSignatoryRole;
    roleLabel: string;
    name: string;
    functionTitle: string;
    credential: string;
    signatureImage: string | null;
    date: string;
  }>;
}

export const NDT_RESPONSIBILITY_TERM =
  "Responsabilizam-se pelas informações aqui contidas, para os devidos efeitos, os signatários abaixo.";

export interface NdtValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Só entra no documento o que o pdfmake REALMENTE desenha: PNG e JPEG.
 *
 * Esta função aceitava `gif` e `webp`. Os dois passavam no guard e derrubavam
 * a geração inteira com "Unknown image format" — o guard existia justamente
 * para impedir isso, e deixava passar. A lista agora bate com o que o motor
 * suporta; a classificação completa (base64 legado, SVG, URL, caminho local)
 * está em `infrastructure/documents/pdf-image.ts`.
 */
export function isEmbeddableImage(dataUrl: string | null | undefined): boolean {
  return typeof dataUrl === "string" && /^data:image\/(png|jpe?g);base64,/i.test(dataUrl);
}

/** "01", "02"... o próximo número livre para uma nova linha de resultado. */
export function suggestNextItemNumber(existing: readonly string[]): string {
  let max = 0;
  for (const value of existing) {
    const n = parseInt(String(value).replace(/[^0-9]/g, ""), 10);
    if (!isNaN(n) && n > max) max = n;
  }
  return String(max + 1).padStart(2, "0");
}

/** Coleções ausentes viram vazias; fotos antigas ganham os campos atuais. */
export function normalizeCommon(report: any, prefix: string): void {
  for (const key of ["photos", "signatories", "history"]) if (!Array.isArray(report[key])) report[key] = [];
  if (report.croqui === undefined) report.croqui = null;
  if (report.frozenSnapshot === undefined) report.frozenSnapshot = null;
  if (report.inspectionId === undefined) report.inspectionId = null;
  if (report.osSubWorkOrderId === undefined) report.osSubWorkOrderId = null;
  if (typeof report.subOsNumber !== "string") report.subOsNumber = "";
  report.photos = report.photos.map((raw: any, index: number) => ({
    key: raw.key || `${prefix}-PHOTO-LEGACY-${index}`,
    image: raw.image ?? "",
    fileName: raw.fileName ?? "",
    fileSize: Number(raw.fileSize ?? 0),
    uploadedAt: raw.uploadedAt ?? "",
    item: raw.item ?? "",
    location: raw.location ?? "",
    comment: raw.comment ?? ""
  }));
}

/** Validações comuns (contexto, procedimento, norma, critério, inspetor, fotos). */
export function validateCommon(report: NdtReportCommon<unknown>, procedureLabel: string): string[] {
  const errors: string[] = [];
  if (!report.equipmentTag.trim()) errors.push("Informe o Equipamento / TAG.");
  if (!report.testDate.trim()) errors.push("Informe a Data do Ensaio.");
  if (!report.procedureId && !report.procedureRevision.trim()) errors.push(`Selecione o Procedimento ${procedureLabel}.`);
  if (!report.referenceNormId && !report.referenceNorm.trim()) errors.push("Selecione a Norma de Referência.");
  if (!report.acceptanceCriteriaId && !report.acceptanceCriteria.trim()) errors.push("Selecione o Critério de Aceitação.");
  if (!report.inspectorProfessionalId && !report.inspectorName.trim()) errors.push("Informe o Inspetor responsável pelo ensaio.");
  const withoutPhotoItem = report.photos.filter(p => !p.item.trim()).length;
  if (withoutPhotoItem > 0) errors.push(`Existem ${withoutPhotoItem} ${withoutPhotoItem === 1 ? "fotografia" : "fotografias"} sem Componente informado.`);
  return errors;
}
